import type { Invoice } from "src/types";
import { InvoiceStatus } from "src/constants/invoice";
import { DEMO_INVOICES } from "./mockData";

export type DemoTx = {
    id: string;
    amount: number;
    currency: string;
    purpose: string | null;
    counterpartName: string | null;
    bookingDate: string;
    // Matches the real FinApiTransaction shape (an array, since a transaction can cover
    // several invoices) — was previously a mismatched singular `matchedInvoiceId` field here,
    // silently cast past with `as any as T` in apiClient.ts, so every demo transaction always
    // read as unmatched no matter what this seed data intended.
    matchedInvoiceIds: string[];
    matchedBy: "auto" | "manual" | null;
    ignored: boolean;
};

type DemoBankState = {
    connected: boolean;
    bankName: string;
    lastSyncAt: string | null;
    accounts: Array<{ id: number; accountName: string; iban: string | null; accountType: string; balance: number; currency: string }>;
    recentTransactions: DemoTx[];
    pendingTransactions: Array<{ id: string; amount: number; currency: string; purpose: string | null; counterpartName: string | null; valueDate: string }>;
};

export type DemoState = {
    seededAt: string;
    invoices: Invoice[];
    bank: DemoBankState;
};

const KEY = "medinvoice_demo_state_v1";

export function clearDemoState() {
    try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

function safeParse<T>(raw: string | null): T | null {
    if (!raw) return null;
    try { return JSON.parse(raw) as T; } catch { return null; }
}

export function loadDemoState(): DemoState {
    const existing = safeParse<DemoState>(sessionStorage.getItem(KEY) ?? localStorage.getItem(KEY));
    // Array.isArray guard also catches state saved before the matchedInvoiceIds array shape
    // existed (or from when this also carried a recentOutgoingTransactions list) — a stale
    // shape would otherwise stick around for the rest of the session instead of reseeding.
    if (existing?.invoices?.length && existing.bank && Array.isArray(existing.bank.recentTransactions)) return existing;

    // Seed invoices (exact scenario is encoded in DEMO_INVOICES)
    const now = new Date();
    const iso = (d: Date) => d.toISOString();
    const daysAgo = (n: number) => new Date(now.getTime() - n * 24 * 60 * 60 * 1000);
    const daysFromNow = (n: number) => new Date(now.getTime() + n * 24 * 60 * 60 * 1000);

    // IMPORTANT: Use relative dates so demo doesn't become "overdue forever".
    const invoices = DEMO_INVOICES.map((i) => {
        const inv = { ...i };

        // Not sent invoices: keep no sent/due dates
        if (!inv.sentDate) {
            inv.creationDate = iso(daysAgo(3));
            return inv;
        }

        // Overdue invoices (M1/M3): due date in the past
        const isEscalated = (inv.dunning ?? 0) >= 1 || inv.status === InvoiceStatus.OVERDUE;
        if (isEscalated && inv.status !== InvoiceStatus.PAID) {
            inv.creationDate = iso(daysAgo(40));
            inv.sentDate = iso(daysAgo(35));
            inv.dueDate = (inv.dunning ?? 0) >= 3 ? iso(daysAgo(25)) : iso(daysAgo(10));
            inv.status = InvoiceStatus.OVERDUE;
            return inv;
        }

        // Normal sent invoices: due date in the future
        inv.creationDate = iso(daysAgo(10));
        inv.sentDate = iso(daysAgo(8));
        inv.dueDate = iso(daysFromNow(14));
        inv.status = inv.status === InvoiceStatus.PAID ? InvoiceStatus.PAID : InvoiceStatus.SENT;
        return inv;
    });

    // Seed BANKSapi mimic:
    // - 6 transactions total
    // - 3 correct references matched (=> 3 paid invoices)
    // - 2 wrong references (unmatched)
    // - 1 irrelevant (lotto win)
    const invPaid = invoices.filter((x) => x.status === InvoiceStatus.PAID).slice(0, 3);
    const txs: DemoTx[] = [
        {
            id: "tx-1",
            amount: invPaid[0]?.amount ?? 485.5,
            currency: "EUR",
            counterpartName: "Patient Zahlung",
            purpose: `Rechnung ${invPaid[0]?.readableId ?? "2024-0001"}`,
            bookingDate: iso(daysAgo(2)),
            matchedInvoiceIds: invPaid[0] ? [invPaid[0].id] : [],
            matchedBy: "auto",
            ignored: false,
        },
        {
            id: "tx-2",
            amount: invPaid[1]?.amount ?? 920,
            currency: "EUR",
            counterpartName: "Überweisung",
            purpose: `RG ${invPaid[1]?.readableId ?? "2024-0006"} Danke`,
            bookingDate: iso(daysAgo(4)),
            matchedInvoiceIds: invPaid[1] ? [invPaid[1].id] : [],
            matchedBy: "auto",
            ignored: false,
        },
        {
            id: "tx-3",
            amount: invPaid[2]?.amount ?? 445.6,
            currency: "EUR",
            counterpartName: "M. Zahlung",
            purpose: `Invoice ${invPaid[2]?.readableId ?? "2024-0007"}`,
            bookingDate: iso(daysAgo(7)),
            matchedInvoiceIds: invPaid[2] ? [invPaid[2].id] : [],
            matchedBy: "auto",
            ignored: false,
        },
        {
            id: "tx-4",
            amount: 198.0,
            currency: "EUR",
            counterpartName: "Überweisung",
            purpose: "Rechnung 2024-9999 (falsche Referenz)",
            bookingDate: iso(daysAgo(1)),
            matchedInvoiceIds: [],
            matchedBy: null,
            ignored: false,
        },
        {
            id: "tx-5",
            amount: 149.9,
            currency: "EUR",
            counterpartName: "Überweisung",
            purpose: "RG 2024-0003 Betrag stimmt nicht",
            bookingDate: iso(daysAgo(3)),
            matchedInvoiceIds: [],
            matchedBy: null,
            ignored: false,
        },
        {
            id: "tx-6",
            amount: 12.5,
            currency: "EUR",
            counterpartName: "LOTTO24",
            purpose: "Lotto Gewinn",
            bookingDate: iso(daysAgo(10)),
            matchedInvoiceIds: [],
            matchedBy: null,
            ignored: true,
        },
    ];

    const bank: DemoBankState = {
        connected: true,
        bankName: "Demo Bank",
        lastSyncAt: iso(daysAgo(0)),
        accounts: [
            { id: 1, accountName: "Praxis Geschäftskonto", iban: "DE89370400440532013000", accountType: "CHECKING", balance: 12840.55, currency: "EUR" },
        ],
        recentTransactions: txs,
        pendingTransactions: [],
    };

    const state: DemoState = { seededAt: new Date().toISOString(), invoices, bank };
    saveDemoState(state);
    return state;
}

export function saveDemoState(state: DemoState) {
    // Keep demo data tab-scoped. This also ensures it disappears when the tab closes.
    try {
        sessionStorage.setItem(KEY, JSON.stringify(state));
    } catch {
        // Storage quota exceeded — silently ignore; demo state may be partially stale
    }
}

export function updateDemoState(mut: (s: DemoState) => DemoState) {
    const next = mut(loadDemoState());
    saveDemoState(next);
    return next;
}

export function applyManualMatch(transactionId: string, invoiceId: string, amount: number) {
    return updateDemoState((s) => {
        const tx = s.bank.recentTransactions.find((t) => t.id === transactionId);
        const inv = s.invoices.find((i) => i.id === invoiceId);
        if (!tx || !inv) return s;

        tx.matchedInvoiceIds = [invoiceId];
        tx.matchedBy = "manual";
        tx.ignored = false;

        inv.status = InvoiceStatus.PAID;
        inv.paidAmount = amount;
        return { ...s };
    });
}

export function applyUnmatch(transactionId: string) {
    return updateDemoState((s) => {
        const tx = s.bank.recentTransactions.find((t) => t.id === transactionId);
        if (!tx) return s;

        const inv = tx.matchedInvoiceIds[0] ? s.invoices.find((i) => i.id === tx.matchedInvoiceIds[0]) : null;
        if (inv) {
            inv.status = inv.sentDate ? InvoiceStatus.SENT : InvoiceStatus.OPEN;
            inv.paidAmount = 0;
        }
        tx.matchedInvoiceIds = [];
        tx.matchedBy = null;
        return { ...s };
    });
}

export function applyIgnore(transactionId: string, ignored: boolean) {
    return updateDemoState((s) => {
        const tx = s.bank.recentTransactions.find((t) => t.id === transactionId);
        if (!tx) return s;
        tx.ignored = ignored;
        return { ...s };
    });
}
